import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { LatLngExpression, Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useEffect, useRef } from 'react';
import { MapProps, Location, MapCenter } from '../types';

const customIcon = new L.Icon({
  iconUrl: 'https://pizzaveg.it/materiale/symbol-pizzaveg-green.png',
  iconSize: [45, 45],
  iconAnchor: [45, 45],
  popupAnchor: [1, -34]
});

function MapEvents({ 
  onBoundsChange, 
  onClick 
}: { 
  onBoundsChange?: MapProps['onBoundsChange'];
  onClick?: MapProps['onClick'];
}) {
  useMapEvents({
    moveend: (e) => {
      if (onBoundsChange) {
        const bounds = e.target.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        onBoundsChange([[sw.lat, sw.lng], [ne.lat, ne.lng]]);
      }
    },
    click: (e) => {
      onClick?.({ lat: e.latlng.lat, lng: e.latlng.lng });
    }
  });
  return null;
}

interface MapUpdaterProps {
  center: MapCenter;
  zoom?: number;
  locations?: Location[];
}

function MapUpdater({ center, zoom, locations }: MapUpdaterProps) {
  const map = useMap();
  
  useEffect(() => {
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    
    if (locations && locations.length > 0) {
      const points = locations.map(loc => [loc.lat, loc.lng] as [number, number]);
      const mapBounds = new L.LatLngBounds(points);
      
      const currentBounds = map.getBounds();
      if (!currentBounds.equals(mapBounds)) {
        map.fitBounds(mapBounds.pad(0.1));
      }
    } else if (
      currentCenter.lat !== center.lat || 
      currentCenter.lng !== center.lng || 
      currentZoom !== (zoom || center.zoom || 6)
    ) {
      map.setView(
        [center.lat, center.lng], 
        zoom || center.zoom || 6, 
        { animate: false }
      );
    }
  }, [
    map,
    center.lat,
    center.lng,
    center.zoom,
    zoom,
    JSON.stringify(locations?.map(loc => [loc.lat, loc.lng]))
  ]);

  return null;
}

export default function Map({ 
  locations = [], 
  onMarkerClick,
  onBoundsChange,
  center = { lat: 41.9028, lng: 12.4964, zoom: 6 },
  onClick
}: MapProps) {
  const initialCenter: LatLngExpression = [center.lat, center.lng];
  const mapRef = useRef<LeafletMap | null>(null);

  return (
    <MapContainer
      center={initialCenter}
      zoom={center.zoom}
      style={{ height: '100%', width: '100%' }}
      ref={mapRef}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <MapEvents onBoundsChange={onBoundsChange} onClick={onClick} />
      <MapUpdater 
        center={center}
        zoom={center.zoom}
        locations={locations}
      />
      {locations.map((location) => (
        <Marker
          key={location.id}
          position={[location.lat, location.lng]}
          icon={customIcon}
          eventHandlers={{
            click: () => onMarkerClick?.(location.id)
          }}
        />
      ))}
    </MapContainer>
  );
}